import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import Accordion from 'modules/views/shared/Accordion';
import AccordionDetails from './components/AccordionDetails';
import Loader from 'modules/views/shared/Loader';
import Image from 'modules/views/shared/Image';
import Select from 'modules/views/shared/Select';
import { StatusChip } from 'modules/views/shared/StatusChip';
import { ReactComponent as DownloadIcon } from 'assets/icons/Download.svg';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import useConsentFormInformation, { ConsentFormSections } from './hooks/useConsentFormInformation';

import { useStyles } from './styles';

const ConsentFormInformation = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { goToPreviousStep } = useWizardStepNavigation();

  const {
    isLoadingInformation,
    isDownloadLoading,
    data,
    consentForms,
    openedAccordions,
    downloadConsentForm,
    setOpenedAccordions,
    assignedCompanies,
    setSelectedEmployer,
    selectedEmployer,
    isWorker,
  } = useConsentFormInformation();

  const content = isLoadingInformation ? (
    <div className={classes.loaderContainer}>
      <Loader styleClass={classes.loader} />
    </div>
  ) : (
    <div>
      {assignedCompanies.length > 1 && (
        <div className={classes.assignedCompaniesContainer}>
          <Select
            inputLabel={t('selectProject.selectEmployer')}
            options={assignedCompanies.map(company => ({
              label: company.name,
              value: company.id,
              workerProjectId: company.workerProjectId,
            }))}
            selectedValue={selectedEmployer}
            testId="consent-info-employer-select"
            onChange={employer => {
              setSelectedEmployer(employer);
            }}
          />
        </div>
      )}
      {Object.entries(data).map(
        ([sectionKey, sectionData]) =>
          (sectionData.fields?.length || sectionData.sections?.length) && (
            <Accordion
              key={sectionKey}
              summary={sectionData.title}
              expanded={openedAccordions[sectionKey]}
              accordionStyleClass={classes.accordion}
              testId={`consent-info-accordion-${sectionKey}`}
              onChange={() =>
                setOpenedAccordions(prev => ({
                  ...prev,
                  [sectionKey]: !prev[sectionKey],
                }))
              }
            >
              <AccordionDetails
                sections={sectionData.sections}
                data={sectionData.fields}
                responsiveValues={sectionData.responsiveValues}
              />
            </Accordion>
          ),
      )}

      {consentForms.map(consentForm => (
        <Accordion
          key={consentForm.key}
          summary={
            <div>
              {consentForm.title}
              <span className={classes.consentFormChip}>
                <StatusChip label={consentForm.chipLabel} variant={consentForm.chipVariant} />
              </span>
            </div>
          }
          expanded={!!openedAccordions[consentForm.key]}
          accordionStyleClass={`${classes.accordion} ${classes.consentFormAccordion}`}
          testId={`consent-info-accordion-${consentForm.key}`}
          onChange={() =>
            setOpenedAccordions(prev => ({
              ...prev,
              [consentForm.key]: !prev[consentForm.key],
            }))
          }
        >
          <div
            className={classes.consentFormContent}
            dangerouslySetInnerHTML={{
              __html: consentForm.htmlContent,
            }}
          />
        </Accordion>
      ))}

      <Accordion
        summary={data[ConsentFormSections.SIGNATURE].title}
        expanded={openedAccordions[ConsentFormSections.SIGNATURE]}
        accordionStyleClass={classes.accordion}
        testId="consent-info-accordion-signature"
        onChange={() =>
          setOpenedAccordions(prev => ({
            ...prev,
            [ConsentFormSections.SIGNATURE]: !prev[ConsentFormSections.SIGNATURE],
          }))
        }
      >
        <Image src={data[ConsentFormSections.SIGNATURE].imageUrl} alt="Signature" className={classes.signatureImage} />
        <p className={classes.signatureDateText}>{data[ConsentFormSections.SIGNATURE].date}</p>
      </Accordion>

      <NavigationButtons
        primaryButtonProps={{
          label: (
            <>
              {t('common.download')}
              <DownloadIcon className={classes.downloadIcon} />
            </>
          ),
          mobileLabel: t('common.download'),
          isLoading: isDownloadLoading,
          hasEndArrow: false,
          styleClass: classes.downloadButton,
          testId: 'consent-info-download-button',
          onClick: downloadConsentForm,
        }}
        secondaryButtonProps={{
          label: t('common.back'),
          testId: 'consent-info-back-button',
          onClick: () => goToPreviousStep(1),
        }}
      />
    </div>
  );

  return (
    <WithWizard
      disableNavigation={isLoadingInformation}
      content={content}
      contentStyleClass={isLoadingInformation ? classes.content : ''}
      showOnlineWarning={!isWorker}
    />
  );
};

export default memo(ConsentFormInformation);
